<template>
  <div
    ref="app-dialer-main-popup"
    data-draggable
    class="app-dialer-main-popup"
    :class="{ [`is-${display}`]: true }"
    :style="componentStyle"
  >
    <div
      class="row primary"
      data-draggable
    >
      <div class="popup-info">
        <base-icon-button
          icon="push_pin"
          data-action="toggle-pin"
          @click="onTogglePin"
        />

        <app-dialer-call-card-info
          :avatar="avatar"
          :agent="agent"
          :contact="contact"
          :identifier="identifier"
          :members="members"
          :is-expanded="isMembersPopupActive"
          :show-info-button="!!description"
          :is-info-button-active="isInfoActive"
          @toggle-info="$emit('toggle-info')"
          @toggle-members="$emit('toggle-members-popup')"
        />
      </div>

      <div
        v-if="display === 'popup'"
        class="divider"
      />

      <app-dialer-call-status
        :timestamp="timestamp"
        :session-id="sessionId"
        :is-connecting="isConnecting"
        :is-in-progress="isInProgress"
      />

      <div
        v-if="display === 'popup'"
        class="divider"
      />

      <app-dialer-call-controls
        :is-on-hold="isOnHold"
        :is-muted="isMuted"
        :is-put-on-hold-action-loading="isPutOnHoldActionLoading"
        :is-hang-up-action-loading="isHangUpActionLoading"
        :is-keypad-active="isKeypadActive"
        :is-invite-button-active="isInvitePopupActive"
        :is-transfer-button-active="isTransferPopupActive"
        :is-connecting="isConnecting"
        :has-keypad-button="hasKeypadButton"
        :has-invite-button="hasInviteButton"
        :has-transfer-button="hasTransferButton"
        :has-hold-button="hasHoldButton"
        :has-mute-button="hasMuteButton"
        :has-contact-member-on-call="hasContactMemberOnCall"
        @toggle-keypad="$emit('toggle-keypad', $event)"
        @toggle-invite="$emit('toggle-invite-popup', $event)"
        @toggle-transfer="$emit('toggle-transfer-popup', $event)"
        @put-on-hold="$emit('put-on-hold', $event)"
        @mute-self="$emit('mute-self', $event)"
        @hang-up="$emit('hang-up', $event)"
      />
    </div>

    <div
      v-if="showGoToButton"
      class="row secondary"
      data-draggable
    >
      <div
        data-button="go-to-session"
        @click="$emit('go-to-session')"
      >
        <or-tooltip :content="$t('hitl.modules.dialer.go-to-session-description')">
          {{ $t('hitl.modules.dialer.go-to-session') }}
        </or-tooltip>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue-demi';
import { OrTooltipV3 as OrTooltip } from '@onereach/ui-components-25';

import { BaseIconButton } from '@/modules/agent-picker';
import {
  AppDialerCallCardInfo,
  AppDialerCallStatus,
  AppDialerCallControls,
} from '../controls';
import { useComponentReposition } from '@/composables/use-component-reposition';
import {
  AVATAR_PROP,
  AGENT_PROP,
  CONTACT_PROP,
  MEMBERS_PROP,
} from '../../constants';
import { CALL_MEMBER_TYPE, CALL_MEMBER_STATUS } from '../../types';

/** TODO: Move "Go To Session" button to a separate component! */
export default defineComponent({
  name: 'AppDialerMainPopup',
  components: {
    BaseIconButton,
    AppDialerCallCardInfo,
    AppDialerCallStatus,
    AppDialerCallControls,
    OrTooltip,
  },
  props: {
    avatar: AVATAR_PROP,
    agent: AGENT_PROP,
    contact: CONTACT_PROP,
    identifier: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    members: MEMBERS_PROP,
    timestamp: {
      type: [Number, String],
      default: 0,
    },
    sessionId: {
      type: String,
      default: '',
    },
    isConnecting: Boolean,
    isInProgress: Boolean,
    isOnHold: Boolean,
    isMuted: Boolean,
    isPutOnHoldActionLoading: Boolean,
    isHangUpActionLoading: Boolean,
    isInfoActive: Boolean,
    isKeypadActive: Boolean,
    isMembersPopupActive: Boolean,
    isInvitePopupActive: Boolean,
    isTransferPopupActive: Boolean,
    hasKeypadButton: Boolean,
    hasInviteButton: Boolean,
    hasTransferButton: Boolean,
    hasHoldButton: Boolean,
    hasMuteButton: Boolean,
    showGoToButton: Boolean,
  },
  emits: [
    'toggle-info',
    'toggle-keypad',
    'toggle-members-popup',
    'toggle-invite-popup',
    'toggle-transfer-popup',
    'put-on-hold',
    'mute-self',
    'hang-up',
    'go-to-session',
  ],
  setup() {
    const {
      applyReposition,
      pin,
      isPinned,
      componentStyle,
    } = useComponentReposition();
    return { applyReposition, pin, isPinned, componentStyle };
  },
  computed: {
    display(): 'inline' | 'popup' {
      return this.isPinned ? 'inline' : 'popup';
    },
    hasContactMemberOnCall(): boolean {
      return this.members.some(member => {
        return member.type === CALL_MEMBER_TYPE.CONTACT
          && member.status === CALL_MEMBER_STATUS.JOINED;
      });
    },
  },
  mounted() {
    const root = this.$refs['app-dialer-main-popup'] as HTMLElement;
    this.applyReposition({
      component: root,
      container: this.getContainingBlock(),
      isPinnedByDefault: true,
      isMoveEventAvailable: (e: MouseEvent) => {
        return (e.target as HTMLDivElement).hasAttribute('data-draggable');
      },
    });
  },
  methods: {
    onTogglePin() {
      this.pin(!this.isPinned);
    },
    getContainingBlock() {
      return document.querySelector('.agent-ui-desktop') as HTMLElement;
    },
  },
});
</script>

<style lang="scss" scoped>
.app-dialer-main-popup {
  display: flex;
  flex-direction: column;
  background: var(--or-c-inverse-surface);
  min-width: 48rem;

  .row {
    display: flex;
    align-items: center;

    &.primary {
      padding: 0 1.5rem;
    }

    &.secondary {
      padding: 0.25rem;
      justify-content: center;
      border-top: 1px solid var(--or-c-on-surface-variant);
    }
  }

  .popup-info {
    display: flex;
    align-items: center;
    flex: 0 0 auto;

    .app-dialer-call-card-info {
      margin-left: 0.75rem;
    }
  }

  .app-dialer-call-status {
    flex: 1 1 100%;
  }

  .app-dialer-call-controls {
    flex: 0 0 auto;
  }

  &.is-inline {
    display: flex;
    border-top: 1px solid var(--or-c-on-surface-variant);

    .row.primary {
      height: 3rem;
    }

    .popup-info {
      flex: 0 0 35%;
      margin-right: 1rem;

      [data-action="toggle-pin"] {
        ::v-deep {
          .or-icon-v3 {
            position: relative;

            &:before {
              content: '';
              background-color: currentColor;
              width: 100%;
              height: 2px;
              position: absolute;
              top: 50%;
              left: 50%;
              z-index: 1;
              transform: translateX(-50%) rotate(45deg);
            }
          }
        }
      }
    }

    .app-dialer-call-controls {
      flex: 0 0 35%;
      justify-content: right;
      margin-left: 1rem;
    }
  }

  &.is-popup {
    display: inline-flex;
    max-width: 60rem;
    border-radius: 0.5rem;
    z-index: 50;
    cursor: grab;

    .row.primary {
      height: 6rem;
      padding: 0 1rem;
    }

    .popup-info,
    .app-dialer-call-status,
    .app-dialer-call-controls {
      cursor: default;
    }

    .popup-info {
      max-width: 50%;
    }
  }

  [data-button="go-to-session"] {
    text-align: center;
    width: 22.5rem;
    color: var(--or-c-inverse-on-surface);
    font-size: var(--fs-0);
    line-height: 1.5;
    user-select: none;
    cursor: pointer;

    &:hover {
      color: var(--or-c-inverse-primary);
    }
  }

  .divider {
    height: 100%;
    flex: 0 0 auto;
    width: 1px;
    margin: 0 1rem;
    background: var(--or-c-on-surface-variant);
  }
}
</style>
